import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Card, Col, Empty, Row, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  MachinerySummaryRow,
  MachinerySource,
  CONSUMABLES_COLOR,
  EQUIPMENT_SHADES,
  NORM_SHADES,
  TOOLS_SHADES,
  SOURCE_COLORS,
  computeParetoAbc,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./machineryShared";

// Two chart cards:
// 1. "Cost Split" — nested two-ring donut. Inner ring = Norm / Tools /
//    Equipment totals plus a muted Consumables slice (so the ring sums to the
//    page total), outer ring = top items per source + an aggregated rest
//    slice, each family in its own colour ramp, and a matching muted
//    consumables filler so the two rings stay arc-aligned. Centre = total
//    machinery cost. Zero-cost sources drop out, so an older backend without
//    the Equipment source renders exactly today's two-family donut.
// 2. "Cost Concentration" — Pareto curve across ALL machinery items (the
//    itemized sources cost-ranked; consumables have no items so they sit
//    outside the curve), 80%/95% ABC cut-offs, same as the manpower report.

const TOP_N_PER_SOURCE = 4;

const truncate = (name: string, max = 22) =>
  name.length > max ? `${name.slice(0, max - 1)}…` : name;

const emptyBody = (
  <div
    style={{
      height: 300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Empty description="No machinery cost data" />
  </div>
);

const sourceLabel = (source: MachinerySource) =>
  source === "NORM" ? "Norm" : source === "EQUIPMENT" ? "Equipment" : "Tools";

const MachineryCostCharts: React.FC<{
  items: MachinerySummaryRow[];
  grandTotal: number;
  normCost: number;
  toolsCost: number;
  equipmentCost?: number;
  consumablesCost: number;
}> = ({
  items,
  grandTotal,
  normCost,
  toolsCost,
  equipmentCost = 0,
  consumablesCost,
}) => {
  // Rows with a real cost only, in API order (already totalCost DESC across
  // both sources). An all-zero payload falls to Empty, never fake slices.
  const ranked = useMemo(
    () =>
      items
        .map((m) => ({
          source: m.source,
          name: m.itemName || m.itemCode || "Unlinked tool",
          cost: num(m.totalCost),
          qty: num(m.totalQuantity),
          uom: m.uomName || "",
        }))
        .filter((m) => m.cost > 0),
    [items]
  );

  const hasData = ranked.length > 0 || consumablesCost > 0;

  // Cumulative % of total cost at each rank + ABC class sizes
  // (A ≈ first 80% of spend, B → 95%, C = the long tail).
  const pareto = useMemo(() => computeParetoAbc(ranked), [ranked]);

  const donutOption = useMemo(() => {
    // Outer ring: top items per source + a rest slice per source, ordered
    // [NORM…, NORM rest, TOOLS…, TOOLS rest, consumables filler]. Both series
    // share the default 90° start angle and each family's outer sum equals
    // its inner slice, so the sub-slices sit exactly over their parent arc.
    const familySlices = (source: MachinerySource, familyTotal: number) => {
      const family = ranked.filter((m) => m.source === source);
      const top = family.slice(0, TOP_N_PER_SOURCE);
      const shades =
        source === "NORM"
          ? NORM_SHADES
          : source === "EQUIPMENT"
          ? EQUIPMENT_SHADES
          : TOOLS_SHADES;
      const slices = top.map((m, i) => ({
        name: m.name,
        value: m.cost,
        qty: m.qty,
        uom: m.uom,
        source: source as string,
        itemStyle: { color: shades[i % shades.length] },
      }));
      const rest = Math.max(
        0,
        familyTotal - top.reduce((sum, m) => sum + m.cost, 0)
      );
      if (rest > 0) {
        slices.push({
          name:
            source === "NORM"
              ? "Other norm items"
              : source === "EQUIPMENT"
              ? "Other equipment norms"
              : "Other tools",
          value: rest,
          qty: 0,
          uom: "",
          source: source as string,
          itemStyle: { color: shades[shades.length - 1] },
        });
      }
      return slices;
    };

    const outer = [
      ...familySlices("NORM", normCost),
      ...familySlices("TOOLS", toolsCost),
      ...familySlices("EQUIPMENT", equipmentCost),
    ];
    if (consumablesCost > 0) {
      // Filler over the consumables inner arc — keeps the rings aligned and
      // gives the bucket a hoverable slice, but it is not an itemized source.
      outer.push({
        name: "Consumables",
        value: consumablesCost,
        qty: 0,
        uom: "",
        source: "",
        itemStyle: { color: "#d9d9d9" },
      });
    }

    // Same family order as `outer` (Norm → Tools → Equipment → Consumables)
    // so the sub-slices sit exactly over their parent arc.
    const inner = [
      {
        name: "Norm",
        value: normCost,
        source: "NORM",
        itemStyle: { color: SOURCE_COLORS.NORM },
      },
      {
        name: "Tools",
        value: toolsCost,
        source: "TOOLS",
        itemStyle: { color: SOURCE_COLORS.TOOLS },
      },
      {
        name: "Equipment",
        value: equipmentCost,
        source: "EQUIPMENT",
        itemStyle: { color: SOURCE_COLORS.EQUIPMENT },
      },
      {
        name: "Consumables",
        value: consumablesCost,
        source: "",
        itemStyle: { color: CONSUMABLES_COLOR },
      },
    ].filter((s) => s.value > 0);

    return {
      animationDuration: 600,
      tooltip: {
        trigger: "item",
        // Keep the tooltip inside the chart box — without this it renders at
        // the cursor and gets clipped by the card / page edges.
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => fmtINR(value),
        formatter: (params: any) => {
          const share = grandTotal > 0 ? (params.value / grandTotal) * 100 : 0;
          const slice = params?.data || {};
          const lines = [
            `<b>${params.name}</b>${
              slice.source ? ` · ${sourceLabel(slice.source)}` : ""
            }`,
            `Cost: ${fmtINRCompact(params.value)} (${fmtINR(params.value)})`,
            `Share: ${share.toFixed(1)}%`,
          ];
          if (slice.qty) {
            lines.splice(
              2,
              0,
              `Quantity: ${fmtQty(slice.qty)}${slice.uom ? ` ${slice.uom}` : ""}`
            );
          }
          if (params.name === "Consumables") {
            lines.push("Tool split materials — not itemized");
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        orient: "horizontal",
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { fontSize: 12, color: "#595959" },
        formatter: (name: string) => truncate(name, 18),
        // Legend lists the outer-ring items; the inner Norm/Tools/Consumables
        // split is already told by the KPI cards and slice colours.
        data: outer.map((s) => s.name),
      },
      series: [
        {
          name: "Source Split",
          type: "pie",
          radius: ["30%", "45%"],
          center: ["50%", "42%"],
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          label: {
            show: true,
            position: "center",
            formatter: `${fmtINRCompact(grandTotal)}\nTotal`,
            fontSize: 15,
            fontWeight: 600,
            color: "#1a1a2e",
            lineHeight: 20,
          },
          labelLine: { show: false },
          data: inner,
        },
        {
          name: "Machinery Items",
          type: "pie",
          radius: ["55%", "75%"],
          center: ["50%", "42%"],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          label: { show: false },
          labelLine: { show: false },
          data: outer,
        },
      ],
    };
  }, [ranked, normCost, toolsCost, equipmentCost, consumablesCost, grandTotal]);

  const paretoOption = useMemo(
    () => ({
      animationDuration: 600,
      grid: { left: 8, right: 24, top: 24, bottom: 8, containLabel: true },
      tooltip: {
        trigger: "axis",
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => `${num(value).toFixed(1)}%`,
        formatter: (paramsList: any) => {
          const p = pareto.points[paramsList?.[0]?.dataIndex];
          if (!p) return "";
          return [
            `<b>Top ${p.rank} machinery item${p.rank === 1 ? "" : "s"}</b>`,
            `${truncate(p.name, 32)} (${sourceLabel(
              p.source
            )}) — ${fmtINRCompact(p.cost)} (${fmtINR(p.cost)})`,
            `Cumulative: ${p.cumPct.toFixed(1)}% · ${fmtINRCompact(
              p.cumCost
            )} (${fmtINR(p.cumCost)})`,
          ].join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        name: "Machinery items by cost rank",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: "#8c8c8c", fontSize: 11 },
        data: pareto.points.map((p) => p.rank),
        axisLine: { lineStyle: { color: "#d9d9d9" } },
        axisTick: { show: false },
        axisLabel: { color: "#8c8c8c" },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        max: 100,
        axisLabel: { color: "#8c8c8c", formatter: "{value}%" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Cumulative cost share",
          type: "line",
          data: pareto.points.map((p) => p.cumPct),
          lineStyle: { color: "#2f54eb", width: 2 },
          itemStyle: { color: "#2f54eb", borderColor: "#fff", borderWidth: 2 },
          symbol: "circle",
          symbolSize: pareto.points.length > 40 ? 4 : 8,
          areaStyle: { color: "rgba(47, 84, 235, 0.08)" },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: { color: "#bfbfbf", type: "dashed" },
            label: {
              color: "#8c8c8c",
              fontSize: 11,
              formatter: "{c}%",
              position: "insideEndTop",
            },
            data: [{ yAxis: 80 }, { yAxis: 95 }],
          },
        },
      ],
    }),
    [pareto]
  );

  const itemizedTotal = useMemo(
    () => pareto.points.reduce((sum, p) => sum + p.cost, 0),
    [pareto]
  );

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title="Cost Split — by Source"
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            hasData ? (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                Inner: source split · Outer: top items
              </span>
            ) : null
          }
        >
          {hasData ? (
            <ReactECharts
              option={donutOption}
              style={{ height: 300, width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
            />
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Cost Concentration (Pareto)
              <Tooltip
                overlayStyle={{ maxWidth: 340 }}
                title={
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      How to read this graph
                    </div>
                    <div>
                      Machinery items are ranked left to right, costliest
                      first. The line shows the running share of itemized
                      machinery cost — at any point, "the top N machinery
                      items together account for X% of spend".
                    </div>
                    <div style={{ marginTop: 4 }}>
                      The steeper the start, the more your machinery spend is
                      concentrated in a few machines. Where the line crosses
                      the dashed 80% mark is your Class A set — the equipment
                      worth the tightest rate and utilisation control. 95%
                      marks Class B; the flat tail after that is Class C.
                    </div>
                    <div style={{ marginTop: 4 }}>
                      Consumables are a lump-sum bucket, so they sit outside
                      this curve.
                    </div>
                    <div style={{ marginTop: 4, color: "#d9d9d9" }}>
                      Hover any point for that item and the running total.
                    </div>
                  </div>
                }
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: 13, cursor: "help" }}
                />
              </Tooltip>
            </span>
          }
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            ranked.length > 0 && pareto.aCount > 0 ? (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                Top {pareto.aCount} of {pareto.points.length} ≈ 80% of{" "}
                {fmtINRCompact(itemizedTotal)}
              </span>
            ) : null
          }
        >
          {ranked.length > 0 ? (
            <>
              <ReactECharts
                option={paretoOption}
                style={{ height: 262, width: "100%" }}
                notMerge={true}
                lazyUpdate={true}
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                }}
              >
                <Tooltip title="Class A: machinery items making up the first ~80% of spend — tightest rate and utilisation control">
                  <Tag color="geekblue" style={{ marginRight: 0 }}>
                    A · {pareto.aCount} item{pareto.aCount === 1 ? "" : "s"}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class B: next ~15% of spend (up to 95%)">
                  <Tag color="cyan" style={{ marginRight: 0 }}>
                    B · {pareto.bCount}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class C: the long tail — last ~5% of spend">
                  <Tag style={{ marginRight: 0 }}>C · {pareto.cCount}</Tag>
                </Tooltip>
              </div>
            </>
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default MachineryCostCharts;
